import { DIRECT_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';
import { getAuthenticatedHttpClient, getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';
import { useState, useEffect, useMemo } from 'react';

const ibmProducts = [
  {
    name: "watsonx Orchestrate",
    description: "Multi-agent orchestration is the next frontier in AI engineering. Get hands-on with the platform connecting AI across 700+ enterprise systems.",
    url: "https://product-link.skills.network/r/linkmaxxers-6",
    keywords: ['agent', 'orchestrat', 'automation', 'workflow', 'assistant', 'chatbot', 'rpa', 'llm', 'genai', 'generative ai'],
  },
  {
    name: "watsonx.governance",
    description: "AI governance is becoming a core skill for every ML engineer. Practice model monitoring, drift detection, and bias evaluation on real workflows.",
    url: "https://product-link.skills.network/r/linkmaxxers-5",
    keywords: ['governance', 'compliance', 'bias', 'audit', 'regulat', 'risk', 'drift', 'fairness', 'responsible ai', 'ethic'],
  },
  {
    name: "watsonx.data",
    description: "Lakehouse architecture is on every senior data engineer JD. Build experience querying open formats across hybrid cloud environments.",
    url: "https://product-link.skills.network/r/linkmaxxers-4",
    keywords: ['data', 'lakehouse', 'warehouse', 'sql', 'query', 'analytics', 'database', 'etl', 'pipeline', 'big data', 'spark', 'parquet'],
  },
  {
    name: "IBM Turbonomic",
    description: "Add FinOps and intelligent resource optimization to your toolkit. Learn how policy-driven automation makes infrastructure decisions at scale.",
    url: "https://product-link.skills.network/r/linkmaxxers-3",
    keywords: ['finops', 'cost', 'optimization', 'resource', 'cloud spend', 'capacity', 'rightsiz', 'utilization', 'scaling'],
  },
  {
    name: "IBM Instana",
    description: "Level up your observability skills — distributed tracing, 1-second metrics, automatic root cause analysis. The capabilities SREs get hired for.",
    url: "https://product-link.skills.network/r/linkmaxxers-2",
    keywords: ['observability', 'monitor', 'tracing', 'apm', 'sre', 'performance', 'metric', 'logs', 'incident', 'reliability', 'uptime'],
  },
  {
    name: "HashiCorp",
    description: "Terraform and Vault are foundational for platform engineers. Build real experience with the tools running production at most Fortune 500s.",
    url: "https://product-link.skills.network/r/linkmaxxers-1",
    keywords: ['terraform', 'vault', 'devops', 'infrastructure as code', 'iac', 'platform engineer', 'secret', 'kubernetes', 'docker', 'ci/cd', 'deploy', 'provision'],
  },
  {
    name: "IBM Bob",
    description: "Step into agentic development — architect mode, repo-wide refactors, and enterprise-grade workflows. The next evolution of AI-assisted coding.",
    url: "https://product-link.skills.network/r/linkmaxxers",
    keywords: ['code', 'developer', 'programming', 'software engineer', 'ide', 'refactor', 'python', 'javascript', 'java', 'typescript', 'react', 'frontend', 'backend', 'api'],
  },
];

const stripHtml = (html) => {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  el.querySelectorAll('.course-staff, .teacher, .prerequisites').forEach((n) => n.remove());
  return el.textContent || el.innerText || '';
};

const DISMISS_COOKIE = 'productPlacementDismissed';
const DISMISS_TTL_SECONDS = 4 * 60 * 60;

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? m[1] : null;
};

const setCookie = (name, value, maxAgeSec) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${value}; max-age=${maxAgeSec}; path=/; SameSite=Lax`;
};

let sharedDismissed = false;

const MATCH_THRESHOLD = 0.5;

const pickProducts = (text) => {
  const bob = ibmProducts.find((p) => p.name === 'IBM Bob');
  const withBob = (list) => (list.some((p) => p.name === 'IBM Bob') ? list : [...list, bob]);

  if (!text) return [bob];
  const lower = text.toLowerCase();
  const scored = ibmProducts.map((product) => ({
    product,
    score: product.keywords.reduce(
      (sum, kw) => sum + (lower.includes(kw) ? 1 : 0),
      0,
    ),
  }));
  const topScore = Math.max(...scored.map((s) => s.score));
  if (topScore === 0) return [bob];
  const threshold = topScore * MATCH_THRESHOLD;
  const matches = scored
    .filter((s) => s.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.product);
  return withBob(matches);
};

const ProductCard = ({ product, onDismiss, index, total, onSelectIndex }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        border: '1px solid #374151',
        borderRadius: 10,
        background: 'rgb(17, 24, 39)',
        padding: '1.5rem',
        margin: '1rem 0',
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: '#9ca3af',
          fontSize: 20,
          lineHeight: 1,
          cursor: 'pointer',
          borderRadius: 4,
        }}
      >
        &times;
      </button>
      <p
        style={{
          color: '#6b7280',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 12,
        }}
      >
        You may be interested in
      </p>
      <p
        style={{
          color: '#ffffff',
          fontWeight: 700,
          fontSize: 22,
          marginBottom: 8,
        }}
      >
        {product.name}
      </p>
      <p
        style={{
          color: '#d1d5db',
          fontSize: 16,
          lineHeight: 1.6,
          marginBottom: 16,
        }}
      >
        {product.description}
      </p>
      <a
        href={product.url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'inline-block',
          color: 'rgb(255 255 255)',
          backgroundColor: hovered ? 'rgb(91 33 182)' : 'rgb(124 58 237)',
          border: `1px solid ${hovered ? 'rgb(91 33 182)' : 'rgb(124 58 237)'}`,
          borderRadius: 6,
          padding: '0.5rem 1rem',
          fontSize: 14,
          fontWeight: 500,
          textDecoration: 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
        }}
      >
        Redeem Your Free Trial &rarr;
      </a>
      {total > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          {Array.from({ length: total }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onSelectIndex(i)}
              aria-label={`Show product ${i + 1}`}
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                background: i === index ? '#60a5fa' : '#4b5563',
                transition: 'background 0.2s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductPlacement = ({ courseId, unitId }) => {
  const [description, setDescription] = useState({ short: '', overview: '' });
  const [unitText, setUnitText] = useState('');
  const [unitLoaded, setUnitLoaded] = useState(false);
  const [isLab, setIsLab] = useState(false);
  const [hasExistingPlacement, setHasExistingPlacement] = useState(false);
  const [dismissed, setDismissedState] = useState(() => {
    const cookieDismissed = getCookie(DISMISS_COOKIE) === '1';
    sharedDismissed = cookieDismissed;
    return cookieDismissed;
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const setDismissed = (val) => {
    sharedDismissed = val;
    if (val) setCookie(DISMISS_COOKIE, '1', DISMISS_TTL_SECONDS);
    setDismissedState(val);
  };

  useEffect(() => {
    const fetchCourseDescription = async () => {
      try {
        const { username } = getAuthenticatedUser();
        const { data } = await getAuthenticatedHttpClient().get(
          `${getConfig().LMS_BASE_URL}/api/courses/v1/courses/${encodeURIComponent(courseId)}/`,
          { params: { username } }
        );
        setDescription({
          short: data.short_description || '',
          overview: data.overview || '',
        });
      } catch (err) {
        console.log('err', err);
      }
    };
    if (courseId) fetchCourseDescription();
  }, [courseId]);

  useEffect(() => {
    if (!unitId) return;

    const fetchUnitContent = async () => {
      try {
        const { data } = await getAuthenticatedHttpClient().get(
          `${getConfig().LMS_BASE_URL}/xblock/${encodeURIComponent(unitId)}`,
          { headers: { Accept: 'application/json' } },
        );
        const html = data || '';
        const hasLtiButton = /data-target="[^"]*lti_launch_handler/.test(html);
        const hasIframe = /<iframe\b(?![^>]*\bsrc="data:)/i.test(html);
        setIsLab(hasLtiButton || hasIframe);
        setHasExistingPlacement(/href="[^"]*(?:ibm\.com|watsonx\.ai|ibm\.biz)/.test(html));
        setUnitText(stripHtml(html));
      } catch (err) {
        console.log('unit fetch err', err);
      } finally {
        setUnitLoaded(true);
      }
    };
    fetchUnitContent();
  }, [unitId]);

  const products = useMemo(
    () => pickProducts(
      `${description.short} ${stripHtml(description.overview)} ${unitText}`,
    ),
    [description, unitText],
  );

  useEffect(() => {
    setCurrentIndex(0);
  }, [products]);

  useEffect(() => {
    if (dismissed || products.length <= 1) return;
    const id = setTimeout(() => {
      setCurrentIndex((i) => (i + 1) % products.length);
    }, 12000);
    return () => clearTimeout(id);
  }, [products.length, dismissed, currentIndex]);

  if (dismissed) return null;
  if (!unitId) return null;
  if (!unitLoaded) return null;
  if (isLab) return null;
  if (hasExistingPlacement) return null;

  return (
    <ProductCard
      product={products[currentIndex]}
      onDismiss={() => setDismissed(true)}
      index={currentIndex}
      total={products.length}
      onSelectIndex={setCurrentIndex}
    />
  );
};

const config = {
  pluginSlots: {
    unit_title_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'custom_unit_title_content',
            type: DIRECT_PLUGIN,
            RenderWidget: ({ courseId, unitId }) => (
              <ProductPlacement courseId={courseId} unitId={unitId} />
            ),
          },
        },
      ]
    },
  },
}

export default config;
